import type { UpdateDraftReportResponse } from '@dte/otw-azure-functions/src/common/interfaces/api/UpdateReportResponse';
import type { DraftCustomerReportData } from '@dte/otw-azure-functions/src/common/interfaces/containers/draftCustomerReports/DraftCustomerReportData';
import { isNonPremiseLocationType } from '@dte/otw-azure-functions/src/common/reports/customerReport/symptomsToCallCodeV3/isNonPremiseLocation';
import type { CustomerReportDataV3 } from '@dte/otw-azure-functions/src/report/CustomerReport';
import { copyJson } from '@dte/otw-utils-core/src/objects/copyJson';
import { deepMergePartial } from '@dte/otw-utils-core/src/objects/deepMergePartial';
import { equalJson } from '@dte/otw-utils-core/src/objects/equalJson';
import { atom } from 'jotai';
import { RESET } from 'jotai/utils';
import { createPendingIdsAtom } from '../../../../../../components/createPendingIdsAtom';
import { ReportStep } from '../../ReportSteps';

export const draftReportAtom = atom<DraftCustomerReportData>();
draftReportAtom.debugLabel = 'draftReport';

const draftReportDefaults: Partial<CustomerReportDataV3> = {
	version: 3,
	channel: 'WISMO',
	answers: {},
	answeredCategories: [],
};

export const workingDraftReportAtom = atom(
	(get) => {
		const storedDraftResponse = get(storedDraftDataAtom);
		const submittedReport = storedDraftResponse?.submittedReport?.data;
		if (submittedReport) {
			return submittedReport;
		}

		const draftReport: Partial<CustomerReportDataV3> = get(draftReportAtom)?.draftReport ?? {};
		const merged = deepMergePartial([draftReportDefaults, draftReport]);
		return merged;
	},
	(get, set, value: Partial<CustomerReportDataV3>) => {
		const currentValue = get(draftReportAtom);
		const newValue = copyJson(currentValue) || {};

		// Merge what's being set in with defaults
		newValue.draftReport = deepMergePartial([draftReportDefaults, value]);

		set(draftReportAtom, newValue);
	},
);
workingDraftReportAtom.debugLabel = 'workingDraftReport';

const storedDraftDataAtomBase = atom<UpdateDraftReportResponse>();
storedDraftDataAtomBase.debugLabel = 'storedDraftDataBase';

function setDefaultCurrentStep(value: DraftCustomerReportData | undefined): void {
	// We don't have a value
	if (!value) {
		return;
	}

	// Current step is already set
	if (value.currentStep) {
		return;
	}

	const locationType = value.draftReport?.location?.type;
	const nonPremise = isNonPremiseLocationType(locationType);

	if (nonPremise) {
		value.currentStep = ReportStep.NonPremise;
	} else {
		value.currentStep = ReportStep.Reporter;
	}
}

export const storedDraftDataAtom = atom(
	(get) => {
		return get(storedDraftDataAtomBase);
	},
	(get, set, value: UpdateDraftReportResponse | typeof RESET) => {
		if (value === RESET) {
			set(storedDraftDataAtomBase, undefined);
			set(draftReportAtom, undefined);
			return;
		}

		// Don't update unless we have a value
		if (value === undefined) {
			return;
		}

		if (value.submittedReport) {
			// Force to confirmation page if already submitted
			console.log('report was submitted');
		}

		// Convert null to undefined
		// Service sends null when we specifically want to clear out values
		if (value.draftReport === null) {
			console.log('Clearing draft report');
			value.draftReport = undefined;
		}

		// If the stored value hasn't changed, we don't need to do anything
		const oldValue = get(storedDraftDataAtomBase);
		if (equalJson(oldValue, value)) {
			return;
		}

		// Set a default value for the current step (if needed)
		setDefaultCurrentStep(value.draftReport);

		// Update both the saved value and the working value
		set(storedDraftDataAtomBase, value);

		const newDraftReport = value.draftReport;
		set(draftReportAtom, newDraftReport);
	},
);
storedDraftDataAtom.debugLabel = 'storedDraftData';

export const draftReportPendingAtom = createPendingIdsAtom();
draftReportPendingAtom.debugLabel = 'pending';

// Clear everything out
export const resetDraftReportAtom = atom(
	// TODO: set a dummy getter to workaround defect in jotai-scope: https://github.com/jotaijs/jotai-scope/issues/62
	(_get) => null,
	(_get, set) => {
		set(storedDraftDataAtom, RESET);
		set(draftReportPendingAtom, RESET);
	},
);
resetDraftReportAtom.debugLabel = 'resetDraftReport';

// Indicate that the form should be blocked from continuing past a step
export const stepBlockedAtom = atom<ReportStep | undefined>(undefined);
stepBlockedAtom.debugLabel = 'stepBlockedAtom';
